import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { verifyBackupCode } from "../services/auth.service";
import { saveAccessToken, saveRefreshToken } from "../utils/storage.utils";
import type { IVerifyBackupCodeRequest } from "../types/auth.types";

interface UseVerifyBackupCodeOptions {
  pendingAccessToken: string;
  onVerified?: () => void;
  onExpired?: () => void;
}

export const useVerifyBackupCode = ({
  pendingAccessToken,
  onVerified,
  onExpired,
}: UseVerifyBackupCodeOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IVerifyBackupCodeRequest) => verifyBackupCode(data, pendingAccessToken),
    onSuccess: async (data) => {
      try {
        if (data.access_token) {
          await saveAccessToken(data.access_token);
        }
        if (data.refresh_token) {
          await saveRefreshToken(data.refresh_token);
        }
      } catch {
        toast.error("Could not save your session. Please try again.");
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["user"] });
      onVerified?.();
    },
    onError: (error) => {
      console.error("Backup code verification failed:", error);

      const status = isAxiosError(error) ? error.response?.status : undefined;
      let errorMessage = "Invalid backup code. Please try again.";

      if (status === 429) {
        errorMessage = "Too many attempts. Please wait a moment and try again.";
      } else if (status === 401) {
        errorMessage = "Verification session expired. Please sign in again.";
      } else {
        const data: unknown = isAxiosError(error) ? error.response?.data : undefined;
        if (
          typeof data === "object" &&
          data != null &&
          "detail" in data &&
          typeof data.detail === "string"
        ) {
          errorMessage = data.detail;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
      }

      toast.error(errorMessage);
      if (status === 401) onExpired?.();
    },
  });
};

export default useVerifyBackupCode;
